// ================================================================
//  EnvironmentStatusDialogController
//  Uniform read-only summary for one environment's status — opened by
//  clicking its status dot on the Biz Interceptors list, same dialog shape
//  for all 4 states: a "time in current state" header, then a Details
//  section whose content varies —
//    ONLINE            : capture counters (captured/uploaded/filtered/dropped)
//    OFFLINE           : none
//    STANDBY           : the specific conditions causing it (standby_reasons)
//    STALLED           : why uploads are paused — the associated agent isn't
//                        taking them: not ONLINE, or STALLED with its local
//                        storage full (also in standby_reasons)
//    AGENT_UNREACHABLE : the connection error + stack trace — same data
//                        Consolidated Stats' "Down" badge shows via
//                        AgentConnectionErrorDialogController, just inside
//                        this same uniform shape instead of a separate one.
//
//  Auto-refresh: polls BizInterceptorService.getById(bizInterceptorId) on
//  frontend.consolidated_stats_refresh_interval (same config key and
//  10s-default fallback as ConsolidatedStatsDialogController's own timer —
//  this dialog shows the same underlying data, just scoped to one
//  environment), silently swapping in fresh data without disturbing
//  anything the user's looking at. Every vm.* field derived from
//  env/monitoringConfig goes through the one rebuild() function below, run
//  once at open and again on every tick, so init and refresh can never
//  drift apart into two different code paths.
//
//  Locals injected:
//    - env : one entry of ic.environments[] (BizInterceptorEnvironmentDTO
//            shape) — env_name, display_status, standby_reasons,
//            stats.{last_state_transition, general.*, agent_connection}
//    - monitoringConfig : the parent interceptor's own
//            InterceptorMonitoringConfig (monitoring_config) — SLA
//            threshold config is interceptor-wide, but each metric's
//            SET/RESET state is tracked per environment_id (see
//            InterceptorHealthMonitoringJobHandler's own javadoc for why),
//            so this environment's own alert flags have to be looked up
//            here rather than living on `env` itself.
//    - bizInterceptorId : the parent interceptor's id — needed to poll for
//            fresh data. Refresh is simply skipped (dialog stays a static
//            snapshot) if a caller doesn't have one to pass.
// ================================================================

angular.module('bizmetryApp').controller('EnvironmentStatusDialogController', function (
    $mdDialog, $mdToast, $interval, $scope, env, monitoringConfig, bizInterceptorId, bizInterceptorName,
    BizInterceptorService, ConfigurationService
) {
    var vm = this;

    // Flips true once via md-on-select on the Lifetime Totals tab (which
    // now also holds the packet distribution pie, side by side with the
    // lifetime tiles) — see packetDistributionPie's own "active" binding
    // comment for why this exists. Deliberately NOT reset by rebuild()
    // below (which runs on every auto-refresh tick): once the user has
    // looked at Lifetime Totals, later refreshes must keep telling that
    // chart it's active, not silently flip it back to hidden.
    vm.lifetimeTotalsTabActive = false;

    // ── SLA alert flags — same lookup ConsolidatedStatsDialogController
    // does (findEnvAlert there) — no entry for this environment_id means
    // implicitly never breached (RESET_CONDITION). ──
    function findEnvAlert(metricConfig, environmentId) {
        if (!metricConfig || !metricConfig.environment_statuses || !environmentId) { return null; }
        var match = metricConfig.environment_statuses.filter(function (s) {
            return s.environment_id === environmentId;
        })[0];
        return (match && match.status === 'SET_CONDITION') ? match : null;
    }

    var STATE_LABELS = {
        ONLINE: 'Uptime',
        OFFLINE: 'Offline for',
        STANDBY: 'In standby for',
        STALLED: 'Stalled for',
        AGENT_UNREACHABLE: 'Unreachable for'
    };

    var DURATION_VARIANT_BY_STATUS = {
        ONLINE: 'success',
        OFFLINE: 'danger',
        STANDBY: 'warning',
        STALLED: 'info',
        AGENT_UNREACHABLE: 'warning'
    };

    function rebuild(newEnv, newMonitoringConfig) {
        vm.env = newEnv || {};
        vm.stats = vm.env.stats || {};
        vm.general = vm.stats.general || {};
        vm.discovery = vm.stats.discovery || {};
        vm.discoveryEnabled = !!((vm.env.parameters || {}).parameters || {}).discovery_mode;
        vm.agentConnection = vm.stats.agent_connection || {};

        var mc = newMonitoringConfig || {};
        var pressureAlert = findEnvAlert(mc.pressure, vm.env.id);
        var latencyAlert = findEnvAlert(mc.agent_latency, vm.env.id);

        vm.pressureAlert = !!pressureAlert;
        vm.pressureAlertSince = pressureAlert ? pressureAlert.last_health_change_ts : null;
        vm.latencyAlert = !!latencyAlert;
        vm.latencyAlertSince = latencyAlert ? latencyAlert.last_health_change_ts : null;

        // Server-computed (see BizInterceptorEnvironmentDTO#restartRequired) —
        // true while a config change couldn't hot-apply (currently only a
        // buffer_max_size change) on at least one of this environment's runtime
        // instances, set on ALL of them at once the moment the change is saved
        // (see BizInterceptorServiceImpl#patch). An OR across every instance,
        // not just one: stays true until EVERY replica has actually been
        // restarted, not just whichever one happens to sync next.
        vm.restartRequired = !!vm.env.restart_required;
        // Raw technical detail (e.g. "buffer_max_size change from 3097 to
        // 9091...") — shown inline in the badge itself now (see
        // environment-status-dialog.html's "Detail: {{ }}" line), not hidden
        // behind a hover tooltip. First flagged instance's reason is enough:
        // every flagged instance of the same environment carries the same one
        // (they were all flagged together by the same config-save, see
        // BizInterceptorServiceImpl#patch).
        vm.restartRequiredDetail = (function () {
            if (!vm.restartRequired) { return null; }
            var flagged = (vm.env.runtime_instances || []).filter(function (ri) { return !!ri.restart_required; })[0];
            return flagged ? flagged.restart_required_reason : null;
        })();

        // Server-computed (see BizInterceptorEnvironmentDTO#uploadFailing) —
        // an OR across every instance's own upload_failing, same shape as
        // restart_required above: one instance's Agent connection recovering
        // must never silently clear this while another instance is still
        // failing to upload. since_ts/error come from whichever
        // currently-failing instance has been failing the longest.
        vm.uploadFailing = !!vm.env.upload_failing;
        vm.uploadFailingSince = vm.env.upload_failing_since_ts;
        vm.uploadFailingDetail = vm.env.last_upload_error;

        vm.hasAnyAlert = vm.pressureAlert || vm.latencyAlert || vm.restartRequired || vm.uploadFailing;
        vm.durationFieldLabel = STATE_LABELS[vm.env.display_status] || 'In this state for';
        vm.durationVariant = DURATION_VARIANT_BY_STATUS[vm.env.display_status] || 'neutral';
    }

    rebuild(env, monitoringConfig);

    // ONLINE reads from stats.session_started_at (set once at the last
    // genuine /init, carried forward unchanged by every /sync since) rather
    // than stats.last_state_transition — that field resets on ANY of the 4
    // display-status flips, so a transient AGENT_UNREACHABLE blip that never
    // actually restarted the interceptor would otherwise make "Uptime" jump
    // back to a few minutes despite heartbeats never having stopped. The
    // other 3 states genuinely DO want "time in the current state" (that's
    // what "Offline for"/"In standby for"/"Unreachable for" mean), so they
    // keep using last_state_transition, which is exactly one state at a time
    // by construction — no separate onlineSince/offlineSince/etc. needed
    // there.
    // Raw "since" timestamp only — formatting (days/hours/minutes, "Unknown"
    // when missing) is the <bm-duration> component's job now, the one
    // shared place that turns a timestamp into "2d 3h 15m" for every such
    // reading across the app instead of each dialog hand-rolling its own.
    vm.durationSince = function () {
        return vm.env.display_status === 'ONLINE'
            ? vm.stats.session_started_at
            : vm.stats.last_state_transition;
    };

    // OFFLINE with no last_state_transition at all means this interceptor
    // has never actually reached the platform once — a materially different
    // situation from "was online and went offline", which gets its own
    // install-instructions card instead of the generic duration badge.
    vm.neverSynced = function () {
        return vm.env.display_status === 'OFFLINE' && !vm.durationSince();
    };

    // "Click here to download" on the never-synced card — same artifact
    // fetch + File System Access API / blob-link delivery as
    // InterceptorDownloadDialogController#download, just fired straight
    // away for this dialog's own (already known) interceptor/environment
    // pair instead of going through that dialog's picker step.
    vm.downloading = false;

    function sanitizeForFileName(value) {
        if (!value) { return 'unknown'; }
        var sanitized = value.trim().replace(/[^a-zA-Z0-9._-]+/g, '-').replace(/-{2,}/g, '-');
        sanitized = sanitized.replace(/^-+|-+$/g, '');
        return sanitized || 'unknown';
    }

    vm.downloadInterceptor = function () {
        if (vm.downloading || !bizInterceptorId || !vm.env.id) { return; }

        // <interceptor name>-<environment>-<currentTimeMillis>.zip — same name the server gives the download
        var finalFileName = sanitizeForFileName(bizInterceptorName) + '-' + sanitizeForFileName(vm.env.env_name || vm.env.name || vm.env.id) + '-' + Date.now() + '.zip';

        function deliver(arrayBuffer) {
            if (typeof window.showSaveFilePicker === 'function') {
                return window.showSaveFilePicker({
                    suggestedName: finalFileName,
                    types: [{ description: 'ZIP archive', accept: { 'application/zip': ['.zip'] } }]
                }).then(function (handle) {
                    return handle.createWritable().then(function (writable) {
                        return writable.write(arrayBuffer).then(function () { return writable.close(); });
                    });
                }).then(function () {
                    return true;
                }).catch(function (err) {
                    // AbortError = the user closed/cancelled the native save dialog.
                    if (err && err.name !== 'AbortError') { throw err; }
                    return false;
                });
            }
            var blob = new Blob([arrayBuffer], { type: 'application/zip' });
            var downloadLink = document.createElement('a');
            downloadLink.href = URL.createObjectURL(blob);
            downloadLink.download = finalFileName;
            downloadLink.click();
            URL.revokeObjectURL(downloadLink.href);
            return true;
        }

        vm.downloading = true;
        BizInterceptorService.downloadArtifact(bizInterceptorId, vm.env.id)
            .then(deliver)
            .then(function (saved) {
                // Close once the file was actually saved (not when the user cancelled the picker).
                if (saved) { $mdDialog.hide(); }
            })
            .catch(function () {
                $mdToast.showSimple('Failed to download artifact.');
            })
            .finally(function () {
                vm.downloading = false;
            });
    };

    // stats.avg_ping_time_ms is already the average — computed server-side
    // on the fly over a trailing window (ConfigKey.INTERCEPTOR_AVG_PING_TIME_WINDOW_MINUTES,
    // default 15 min), not a running lifetime accumulator, so there's
    // nothing left to divide here. Null means no ping sample landed in that
    // window at all — same "—" Consolidated Stats' own avgPingTimeLabel
    // shows for that case (see ConsolidatedStatsDialogController#avgPingTimeLabel).
    vm.avgPingTimeLabel = function () {
        if (vm.stats.avg_ping_time_ms === null || vm.stats.avg_ping_time_ms === undefined) { return '—'; }
        return Math.round(vm.stats.avg_ping_time_ms) + ' ms';
    };

    // Same shape as vm.avgPingTimeLabel above, for the 0-100 load_pressure
    // gauge (see BizInterceptorServiceImpl#resolveAvgLoadPressure) — shown
    // alongside its own indicator bar in the template.
    vm.avgLoadPressureLabel = function () {
        if (vm.stats.avg_load_pressure === null || vm.stats.avg_load_pressure === undefined) { return '—'; }
        return Math.round(vm.stats.avg_load_pressure) + '%';
    };

    // Same shape as vm.avgPingTimeLabel/vm.avgLoadPressureLabel above — a
    // windowed average (see BizInterceptorServiceImpl#resolveAvgReceiveRateKbMin/
    // #resolveAvgUploadRateKbMin) persisted once per sync, not a running
    // lifetime accumulator. Replaces the old Bytes Received/Uploaded tiles.
    vm.avgReceiveRateLabel = function () {
        if (vm.stats.avg_receive_rate_kb_min === null || vm.stats.avg_receive_rate_kb_min === undefined) { return '—'; }
        return vm.stats.avg_receive_rate_kb_min.toFixed(1) + ' KB/min';
    };
    vm.avgUploadRateLabel = function () {
        if (vm.stats.avg_upload_rate_kb_min === null || vm.stats.avg_upload_rate_kb_min === undefined) { return '—'; }
        return vm.stats.avg_upload_rate_kb_min.toFixed(1) + ' KB/min';
    };

    // Same shape as vm.avgReceiveRateLabel/vm.avgUploadRateLabel above, for
    // the frames/minute rates (see BizInterceptorServiceImpl#resolveAvgCapturedRatePerMin/
    // #resolveAvgFilteredRatePerMin/#resolveAvgDroppedRatePerMin) — replaces
    // the old Captured/Filtered/Dropped lifetime-total tiles.
    vm.avgFrameRateLabel = function (framesPerMin) {
        if (framesPerMin === null || framesPerMin === undefined) { return '—'; }
        return framesPerMin.toFixed(1) + '/min';
    };

    // How many of this environment's runtime instances are currently
    // RUNNING — same "read status straight off the model, don't
    // recompute" rule as everywhere else instance status is shown (see
    // ConsolidatedStatsDialogController's own instances_online).
    vm.liveInstancesCount = function () {
        return (vm.env.runtime_instances || []).filter(function (ri) { return ri.status === 'RUNNING'; }).length;
    };

    // Same shape as vm.avgLoadPressureLabel above — now a windowed average
    // computed server-side from bizmetry_biz_interceptor_time_series (see
    // BizInterceptorServiceImpl#resolveWindowedAverages), persisted once per
    // sync, instead of the old live-snapshot SUM across runtime_instances[].
    // Trades an instantaneous (and, like every other windowed gauge here,
    // blended-across-instances) figure for a smoothed one on the same
    // trailing window as Pressure/Ping Time. Null means no sample landed in
    // the window at all (all instances on an SDK build that predates this
    // field, or none synced recently).
    vm.avgActiveWorkerCountLabel = function () {
        var v = vm.stats.avg_active_worker_count;
        return (v === null || v === undefined) ? '—' : Math.round(v);
    };

    // Same shape as vm.avgActiveWorkerCountLabel above, for the backpressure
    // auto-tuner's effective sync batch size — replaces the old
    // live-snapshot SUM across runtime_instances[].
    vm.avgTransmitBatchSizeLabel = function () {
        var v = vm.stats.avg_transmit_batch_size;
        return (v === null || v === undefined) ? '—' : Math.round(v);
    };

    // Both server-computed now (see BizInterceptorServiceImpl#
    // resolveAvgUploadResponsiveness/#resolveAvgHoggingRate) — same as
    // ConsolidatedStatsDialogController's own copies, this controller no
    // longer does any ratio arithmetic, just formats vm.stats' own fields.
    vm.avgUploadResponsivenessLabel = function () {
        var v = vm.stats.avg_upload_responsiveness;
        return (v === null || v === undefined) ? '—' : Math.round(v) + '%';
    };
    // Same 0-100 range/shape as avgUploadResponsivenessLabel above.
    vm.avgHoggingRateLabel = function () {
        var v = vm.stats.avg_hogging_rate;
        return (v === null || v === undefined) ? '—' : Math.round(v) + '%';
    };

    // Same trend-arrow logic as ConsolidatedStatsDialogController's own
    // vm.trendIcon — see that function's comment.
    vm.trendIcon = function (trend) {
        if (trend === 'UP') { return 'arrow_upward'; }
        if (trend === 'DOWN') { return 'arrow_downward'; }
        return null;
    };

    // ========================================
    // AUTO-REFRESH — re-fetches the parent interceptor (same
    // buildEnvironmentsResponse() shape the list uses) and picks this one
    // environment back out of it, so live_status/stats/alerts don't go
    // stale while the dialog is open. Silent — no loading spinner, data
    // just swaps in place. Skipped entirely if no bizInterceptorId was
    // passed in (defensive — leaves this a static snapshot rather than
    // erroring for any caller that predates this).
    // ========================================

    var refreshInterval = ConfigurationService.getFromCache('frontend.consolidated_stats_refresh_interval') || 10000;

    function refreshFromServer() {
        BizInterceptorService.getById(bizInterceptorId, { skipLoader: true })
            .then(function (response) {
                var data = response && response.data;
                if (!data) { return; }
                var freshEnv = (data.environments || []).filter(function (e) { return e.id === vm.env.id; })[0];
                if (freshEnv) { rebuild(freshEnv, data.monitoring_config); }
            })
            .catch(function (error) {
                console.error('[EnvironmentStatusDialog] Error refreshing environment stats:', error);
            });
    }

    // Fired once right away (not just on the first $interval tick, up to
    // refreshInterval later) — the env/monitoringConfig locals this dialog
    // opened with can already be stale (the list they came from refreshes on
    // its own timer), so without this the dialog would show that same stale
    // snapshot for up to refreshInterval before self-correcting.
    if (bizInterceptorId) { refreshFromServer(); }

    var autoRefreshTimer = bizInterceptorId ? $interval(refreshFromServer, refreshInterval) : null;

    $scope.$on('$destroy', function () {
        if (autoRefreshTimer) { $interval.cancel(autoRefreshTimer); }
    });

    vm.cancel = function () { $mdDialog.cancel(); };
});
